import React, { useState, useContext, useEffect } from 'react';
import { Card, Row, Col, Typography, Space, Statistic, Layout, Button, Modal, Form, Input, Spin, message, Dropdown } from 'antd';
import { TeamOutlined, PlusOutlined, EditOutlined, MoreOutlined, LogoutOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useBranches } from '../../hooks/useBranches';
import { useDispatch } from 'react-redux';
import { addBranch, updateBranch, setSelectedBranch } from '../../store/slices/branchesSlice';
import { JWTContext } from '../../contexts/jwtContext';
import './styles.css';

const { Title } = Typography;
const { Content } = Layout;

const SportsBranches = () => {
    const { branches, loading, refetchBranches } = useBranches();
    const [isAddModalVisible, setIsAddModalVisible] = useState(false);
    const [isEditModalVisible, setIsEditModalVisible] = useState(false);
    const [editingBranch, setEditingBranch] = useState(null);
    const [addForm] = Form.useForm();
    const [editForm] = Form.useForm();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { logout } = useContext(JWTContext);
    const [windowSize, setWindowSize] = useState({
        width: window.innerWidth,
        height: window.innerHeight
    });

    // Window size'ı takip et
    useEffect(() => {
        const handleResize = () => {
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight
            });
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const isMobile = windowSize.width < 768;

    const handleBranchSelect = (branchId) => {
        const selectedBranch = branches.find(branch => branch.id === branchId);
        if (selectedBranch) {
            dispatch(setSelectedBranch(selectedBranch));
        }
        navigate('/dashboard');
    };

    const showAddModal = () => {
        setIsAddModalVisible(true);
    };

    const showEditModal = (branch) => {
        setEditingBranch(branch);
        editForm.setFieldsValue({
            name: branch.name
        });
        setIsEditModalVisible(true);
    };

    const handleAddCancel = () => {
        addForm.resetFields();
        setIsAddModalVisible(false);
    };

    const handleEditCancel = () => {
        editForm.resetFields();
        setEditingBranch(null);
        setIsEditModalVisible(false);
    };

    const handleAddSubmit = async (values) => {
        try {
             await dispatch(addBranch({
                name: values.name
            })).unwrap();
            
            message.success('Branş başarıyla eklendi!');
            refetchBranches();
            handleAddCancel();
        } catch (error) {
            console.error('Branch add error details:', error);
            message.error(`Branş eklenirken bir hata oluştu: ${error.message || error}`);
        }
    };

    const handleEditSubmit = async (values) => {
        try {
            await dispatch(updateBranch({
                id: editingBranch.id,
                name: values.name
            })).unwrap();
            
            message.success('Branş başarıyla güncellendi!');
            refetchBranches();
            handleEditCancel();
        } catch (error) {
            message.error('Branş güncellenirken bir hata oluştu!');
            console.error('Branch update error:', error);
        }
    };


    const handleLogout = () => {
        logout();
        localStorage.clear();
        navigate('/login');
    };

    const getDropdownItems = (branch) => [
        {
            key: 'edit',
            label: 'Düzenle',
            icon: <EditOutlined />,
            onClick: () => {
                showEditModal(branch);
            }
        },
        // {
        //     key: 'delete',
        //     label: (
        //         <Popconfirm
        //             title="Branş Sil"
        //             description="Bu branşı silmek istediğinizden emin misiniz?"
        //             onConfirm={() => handleDelete(branch.id)}
        //             onCancel={() => {}}
        //             okText="Evet"
        //             cancelText="Hayır"
        //             okType="danger"
        //         >
        //             <span style={{ color: '#ff4d4f' }}>Sil</span>
        //         </Popconfirm>
        //     ),
        //     icon: <DeleteOutlined />,
        //     danger: true
        // }
    ];

    if (loading && branches.length === 0) {
        return (
            <Layout className="sports-layout">
                <Content className="sports-content">
                    <div className="sports-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                        <Spin size="large" />
                    </div>
                </Content>
            </Layout>
        );
    }

    return (
        <Layout className="sports-layout">
            <Content className="sports-content">
                <div className="sports-container">
                    <Space direction="vertical" size="large" style={{ width: '100%' }}>
                        {/* Responsive başlık ve butonlar */}
                        <div style={{ 
                            display: 'flex', 
                            flexDirection: isMobile ? 'column' : 'row',
                            justifyContent: 'space-between', 
                            alignItems: isMobile ? 'stretch' : 'center',
                            gap: isMobile ? '16px' : '0'
                        }}>
                            <Title level={2} style={{ 
                                textAlign: isMobile ? 'center' : 'left',
                                margin: 0
                            }}>
                                Lütfen Spor Branşı Seçiniz
                            </Title>
                            <Space direction={isMobile ? 'vertical' : 'horizontal'} style={{ width: isMobile ? '100%' : 'auto' }}>
                                <Button 
                                    type="primary" 
                                    icon={<PlusOutlined />}
                                    onClick={showAddModal}
                                    loading={loading}
                                    style={{ width: isMobile ? '100%' : 'auto' }}
                                >
                                    Yeni Branş Ekle
                                </Button>
                                <Button 
                                    type="default" 
                                    icon={<LogoutOutlined />}
                                    onClick={handleLogout}
                                    style={{ width: isMobile ? '100%' : 'auto' }}
                                >
                                    Çıkış Yap
                                </Button>
                            </Space>
                        </div>

                        <Row gutter={[16, 16]} justify="center">
                            {branches.map(branch => (
                                <Col xs={20} sm={12} md={8} lg={6} key={branch.id}>
                                    <div style={{ position: 'relative' }}>
                                        <Card
                                            hoverable
                                            className="branch-card"
                                            onClick={() => handleBranchSelect(branch.id)}
                                        >
                                            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                                <Title level={3} style={{ textAlign: 'center', margin: 0 }}>
                                                    {branch.name}
                                                </Title>
                                                <Row gutter={[8, 8]} justify="center">
                                                    <Col span={12}>
                                                        <Statistic
                                                            title="Aktif Öğrenci"
                                                            value={branch.activeMemberCount || 0}
                                                            prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                                                            valueStyle={{ color: '#52c41a' }}
                                                        />
                                                    </Col>
                                                    <Col span={12}>
                                                        <Statistic
                                                            title="Pasif Öğrenci"
                                                            value={branch.passiveMemberCount || 0}
                                                            prefix={<CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
                                                            valueStyle={{ color: '#ff4d4f' }}
                                                        />
                                                    </Col>
                                                </Row>
                                                <Statistic
                                                    title="Toplam Öğrenci"
                                                    value={(branch.activeMemberCount || 0) + (branch.passiveMemberCount || 0)}
                                                    prefix={<TeamOutlined />}
                                                    style={{ textAlign: 'center' }}
                                                />
                                            </Space>
                                        </Card>
                                        <div 
                                            style={{ 
                                                position: 'absolute', 
                                                top: 8, 
                                                right: 8,
                                                zIndex: 10
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <Dropdown
                                                menu={{ items: getDropdownItems(branch) }}
                                                trigger={['click']}
                                            >
                                                <Button 
                                                    type="text" 
                                                    icon={<MoreOutlined />}
                                                    size="small"
                                                    style={{ 
                                                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                                        border: '1px solid #d9d9d9'
                                                    }}
                                                />
                                            </Dropdown>
                                        </div>
                                    </div>
                                </Col>
                            ))}
                        </Row>

                        {branches.length === 0 && !loading && (
                            <div style={{ textAlign: 'center', padding: '40px' }}>
                                <Title level={4} type="secondary">
                                    Henüz branş bulunmamaktadır. Yeni branş ekleyerek başlayın.
                                </Title>
                            </div>
                        )}
                    </Space>

                    {/* Add Modal */}
                    <Modal
                        title="Yeni Spor Branşı Ekle"
                        open={isAddModalVisible}
                        onCancel={handleAddCancel}
                        footer={null}
                    >
                        <Form
                            form={addForm}
                            layout="vertical"
                            onFinish={handleAddSubmit}
                        >
                            <Form.Item
                                name="name"
                                label="Branş Adı"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Lütfen branş adını giriniz!',
                                    },
                                ]}
                            >
                                <Input placeholder="Branş adını giriniz" />
                            </Form.Item>
                            <Form.Item>
                                <Space>
                                    <Button type="primary" htmlType="submit" loading={loading}>
                                        Ekle
                                    </Button>
                                    <Button onClick={handleAddCancel}>
                                        İptal
                                    </Button>
                                </Space>
                            </Form.Item>
                        </Form>
                    </Modal>

                    {/* Edit Modal */}
                    <Modal
                        title="Branş Düzenle"
                        open={isEditModalVisible}
                        onCancel={handleEditCancel}
                        footer={null}
                    >
                        <Form
                            form={editForm}
                            layout="vertical"
                            onFinish={handleEditSubmit}
                        >
                            <Form.Item
                                name="name"
                                label="Branş Adı"
                                rules={[
                                    {
                                        required: true,
                                        message: 'Lütfen branş adını giriniz!',
                                    },
                                ]}
                            >
                                <Input placeholder="Branş adını giriniz" />
                            </Form.Item>
                            <Form.Item>
                                <Space>
                                    <Button type="primary" htmlType="submit" loading={loading}>
                                        Güncelle
                                    </Button>
                                    <Button onClick={handleEditCancel}>
                                        İptal
                                    </Button>
                                </Space>
                            </Form.Item>
                        </Form>
                    </Modal>
                </div>
            </Content>
        </Layout>
    );
};

export default SportsBranches; 